
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTimesEditor } from "./hooks/useTimesEditor";
import TimesPreview from "./TimesPreview";
import GeneralSettings from "./GeneralSettings";
import TimesManagement from "./TimesManagement";
import { usePageContext } from "@/contexts/PageContext";
import FontSizeSelector, { FontSizeOption } from '@/components/ui/FontSizeSelector';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

interface TimesEditorMainProps {
  content?: any;
  onContentChange?: (field: string, value: any) => void;
}

export const TimesEditorMain: React.FC<TimesEditorMainProps> = ({ content, onContentChange }) => {
  const { currentUser } = usePageContext();
  
  const {
    blockTitle,
    times,
    activeTab,
    isSaving,
    isLoading,
    hasUnsavedChanges,
    setActiveTab,
    handleTimeChange,
    handleUpdateBlockTitle,
    handleAddTime,
    handleUpdateTime,
    handleDeleteTime,
    handleSaveChanges
  } = useTimesEditor({
    content,
    onContentChange,
    currentUserId: currentUser?.id
  });

  const fontSize: FontSizeOption = content?.fontSize || 'md';

  const handleFontSizeChange = (value: FontSizeOption) => {
    onContentChange && onContentChange('fontSize', value);
  };

  // Map FontSizeOption to the expected format for TimesPreview
  const mapFontSizeToPreview = (size: FontSizeOption): 'small' | 'medium' | 'large' => {
    switch (size) {
      case 'sm':
        return 'small';
      case 'md':
        return 'medium';
      case 'lg':
        return 'large';
      default:
        return 'medium';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Label htmlFor="fontSize" className="text-sm font-medium">גודל טקסט</Label>
          <Select value={fontSize} onValueChange={handleFontSizeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="בחר גודל טקסט" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">קטן</SelectItem>
              <SelectItem value="medium">בינוני</SelectItem>
              <SelectItem value="large">גדול</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => setActiveTab('edit')} className={activeTab === 'edit' ? 'bg-primary text-primary-foreground' : ''}>
            עריכה
          </Button>
          <Button variant="outline" onClick={() => setActiveTab('preview')} className={activeTab === 'preview' ? 'bg-primary text-primary-foreground' : ''}>
            תצוגה מקדימה
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit">עריכה</TabsTrigger>
          <TabsTrigger value="preview">תצוגה מקדימה</TabsTrigger>
        </TabsList>
        
        <TabsContent value="edit" className="space-y-6">
          <GeneralSettings 
            blockTitle={blockTitle} 
            onUpdateTitle={handleUpdateBlockTitle} 
          />
          
          <TimesManagement 
            times={times}
            onAddTime={handleAddTime}
            onUpdateTime={handleUpdateTime}
            onDeleteTime={handleDeleteTime}
          />
          
          <div className="flex justify-end">
            <Button 
              onClick={handleSaveChanges}
              disabled={isSaving || !hasUnsavedChanges}
              variant={hasUnsavedChanges ? "default" : "outline"}
            >
              {isSaving ? "שומר..." : "שמור שינויים"}
            </Button>
          </div>
        </TabsContent>
        
        <TabsContent value="preview">
          <TimesPreview 
            content={{
              times,
              blockTitle,
              fontSize: mapFontSizeToPreview(fontSize)
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TimesEditorMain;
